"use client"

import { useEffect } from "react"
import { ArrowRight, Check, Compass, X } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { cn } from "@/common/lib/utils"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"
import { TOUR_STEPS } from "./tour-steps"

/**
 * The guided path, as a slim bar rather than a modal.
 *
 * A modal would block the very screen it is describing, and a coach-mark
 * overlay would need anchor positions in five different views. A bar at the
 * bottom leaves every surface fully usable and readable while it is up, which
 * matters because the whole point is to get the viewer looking at the view
 * behind it.
 *
 * `z-40` on purpose: the LMS shell is a Dialog at `z-50`, so opening the
 * learning portal from the last step covers this rather than fighting it.
 */
export default function DemoTour({ state }: { state: LernpfadDemoState }) {
    const { tourStep, nextTourStep, endTour, localizer } = state
    const { setPersona, setPensumOverride, resetPolicy, toggleFramework } = state
    const { t } = localizer

    // Each step owns the view it belongs to, and two of them own a lever as
    // well. Depending on the individual actions rather than on `state` is what
    // keeps this from re-firing every render: they are all `useCallback`- or
    // `useState`-stable, whereas the state object is a fresh literal each time.
    useEffect(() => {
        if (tourStep === null) return
        const step = TOUR_STEPS[tourStep]
        setPersona(step.persona)
        step.apply?.({ setPensumOverride, resetPolicy, toggleFramework })
    }, [tourStep, setPersona, setPensumOverride, resetPolicy, toggleFramework])

    if (tourStep === null) return null

    const step = TOUR_STEPS[tourStep]
    const isLast = tourStep === TOUR_STEPS.length - 1

    return (
        <div className="pointer-events-none fixed inset-x-0 bottom-0 z-40 flex justify-center p-4">
            <div className="pointer-events-auto flex w-full max-w-3xl items-start gap-4 rounded-xl border bg-background/95 p-4 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/85">
                <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Compass className="size-4.5" />
                </span>

                <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-muted-foreground">
                        {t("tour.step", { current: tourStep + 1, total: TOUR_STEPS.length })}
                    </p>
                    <h3 className="mt-0.5 font-semibold leading-snug">
                        {t(`tour.${step.id}.title` as never)}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                        {t(`tour.${step.id}.body` as never)}
                    </p>

                    {/* Progress: a dot per step, so the length of the path is
                        visible up front rather than a surprise at step five. */}
                    <div className="mt-3 flex items-center gap-1.5">
                        {TOUR_STEPS.map((entry, index) => (
                            <span
                                key={entry.id}
                                className={cn(
                                    "h-1 rounded-full transition-all",
                                    index === tourStep
                                        ? "w-6 bg-primary"
                                        : index < tourStep
                                          ? "w-1.5 bg-primary/40"
                                          : "w-1.5 bg-muted-foreground/20",
                                )}
                            />
                        ))}
                    </div>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={endTour}>
                        <X className="size-3.5" />
                        {t("tour.skip")}
                    </Button>
                    <Button size="sm" onClick={isLast ? endTour : nextTourStep}>
                        {isLast ? <Check className="size-3.5" /> : null}
                        {isLast ? t("tour.done") : t("tour.next")}
                        {isLast ? null : <ArrowRight className="size-3.5" />}
                    </Button>
                </div>
            </div>
        </div>
    )
}
